/**
 * Plan-tier detection and account identity helpers.
 *
 * Lifted verbatim out of Sidebar.tsx during the redesign. This is billing
 * logic — which tier a subscription resolves to drives the upsell CTA and
 * feature gating — so it is isolated here where a visual change cannot touch
 * it, and so the header and user menu can share one implementation.
 */

export type PlanTier = "free" | "payg" | "basic" | "pro";

export function getNameInitials(name: string): string {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function getInitials(email: string | undefined): string {
  if (!email) return "?";
  const local = email.split("@")[0];
  const parts = local.split(/[._-]/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

export const PLAN_ID_TO_TIER: Record<number, Exclude<PlanTier, "free">> = {
  1: "pro",
  2: "basic",
  3: "payg",
};

export const PRICE_TO_TIER: Record<number, Exclude<PlanTier, "free">> = {
  19.99: "pro",
  4.99: "basic",
  0.99: "payg",
  1: "payg",
};

export function getPlanTier(currentPlan: any, apiPlans?: any[]): PlanTier {
  if (!currentPlan) return "free";

  // 1. Try direct subscriptionPlanId match (most reliable)
  const planId = currentPlan.subscriptionPlanId != null
    ? Number(currentPlan.subscriptionPlanId)
    : null;
  if (planId !== null && PLAN_ID_TO_TIER[planId]) {
    return PLAN_ID_TO_TIER[planId];
  }

  // 2. Try planCode / code string
  let code = (currentPlan.planCode ?? currentPlan.code ?? "").toLowerCase();
  if (!code && apiPlans?.length && planId != null) {
    const matched = apiPlans.find(
      (p) =>
        String(p.subscriptionPlanId) === String(planId) ||
        String(p.subscriptionId) === String(planId) ||
        String(p.id) === String(planId)
    );
    code = (matched?.code ?? "").toLowerCase();
  }
  if (code === "pro") return "pro";
  if (code === "basic") return "basic";
  if (code === "pay-as-you-go" || code === "payg") return "payg";

  // 3. Fallback: match by price
  const price = currentPlan.price != null ? Number(currentPlan.price) : null;
  if (price !== null && PRICE_TO_TIER[price]) {
    return PRICE_TO_TIER[price];
  }

  return "free";
}

export function getPlanCTA(tier: PlanTier): {
  label: string;
  sublabel: string;
  buttonLabel: string | null;
  buttonPath: string | null;
  iconColor: string;
  badgeClass: string;
  buttonClass: string;
} {
  switch (tier) {
    case "free":
      return {
        label: "Free Plan",
        sublabel: "Unlock all features",
        buttonLabel: "Upgrade ✦",
        buttonPath: "/payments",
        iconColor: "text-indigo-300",
        badgeClass: "bg-indigo-500/20 border-indigo-500/25",
        buttonClass: "bg-indigo-500/25 hover:bg-indigo-500/40 text-indigo-200 hover:text-white border-indigo-500/35",
      };
    case "payg":
      return {
        label: "Pay As You Go",
        sublabel: "Switch to fixed plan",
        buttonLabel: "Go Pro ✦",
        buttonPath: "/payments",
        iconColor: "text-amber-300",
        badgeClass: "bg-amber-500/15 border-amber-500/25",
        buttonClass: "bg-amber-500/20 hover:bg-amber-500/35 text-amber-200 hover:text-white border-amber-500/30",
      };
    case "basic":
      return {
        label: "Basic Plan",
        sublabel: "More power available",
        buttonLabel: "Go Pro ✦",
        buttonPath: "/payments",
        iconColor: "text-indigo-300",
        badgeClass: "bg-indigo-500/15 border-indigo-500/25",
        buttonClass: "bg-indigo-500/20 hover:bg-indigo-500/35 text-indigo-200 hover:text-white border-indigo-500/30",
      };
    case "pro":
      return {
        label: "Pro Plan",
        sublabel: "Most powerful plan",
        buttonLabel: null,
        buttonPath: null,
        iconColor: "text-violet-300",
        badgeClass: "bg-violet-500/15 border-violet-500/25",
        buttonClass: "",
      };
  }
}

export const GROUP_META: Record<string, { label: string; iconBoxClass: string; iconClass: string }> = {
  account: {
    label: "Account",
    iconBoxClass: "bg-indigo-500/20 group-hover/item:bg-indigo-500/30",
    iconClass: "text-indigo-300 group-hover/item:text-indigo-200",
  },
  help: {
    label: "Support & Help",
    iconBoxClass: "bg-teal-500/20 group-hover/item:bg-teal-500/30",
    iconClass: "text-teal-300 group-hover/item:text-teal-200",
  },
};

export function getPlanLabel(currentPlan: any, apiPlans?: any[]): string {
  if (!currentPlan) return "Free Plan";
  // Use the same tier logic for a consistent label
  const tier = getPlanTier(currentPlan, apiPlans);
  switch (tier) {
    case "pro": return "Pro";
    case "basic": return "Basic";
    case "payg": return "Pay As You Go";
    default: return "Free Plan";
  }
}


/** Best display name we can derive, capitalised. */
export function getDisplayName(profile: any): string {
    const email = profile?.email ?? "";
    const raw = profile?.name || email.split("@")[0] || "User";
    return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/** Initials for the avatar — prefers the real name over the email local part. */
export function getAvatarInitials(profile: any): string {
    return profile?.name ? getNameInitials(profile.name) : getInitials(profile?.email);
}
